import { useCallback, useMemo, useReducer } from "react";
import { LinkedinSalesNavigatorFilters } from "../../__generated__/graphql";
import LsnAutopilotHydrationContext, {
  ILsnAutopilotHydrationValues,
} from "../../contexts/LsnAutopilotHydrationContext";

type State = {
  prompt: string;
  savedFilters: LinkedinSalesNavigatorFilters | null;
  expandFilters: LinkedinSalesNavigatorFilters | null;
  expandPrompt: string;
  scoringCriteriaValues: string[];
  expandScoringCriteriaValues: string[];
  specificCompanies: string[];
};

type Action =
  | { type: "SET_PROMPT"; payload: string }
  | { type: "SET_SAVED_FILTERS"; payload: LinkedinSalesNavigatorFilters | null }
  | {
      type: "SET_EXPAND_FILTERS";
      payload: LinkedinSalesNavigatorFilters | null;
    }
  | { type: "SET_EXPAND_PROMPT"; payload: string }
  | { type: "SET_EXPAND_SCORING_CRITERIA_VALUES"; payload: string[] }
  | { type: "SET_SCORING_CRITERIA_VALUES"; payload: string[] }
  | { type: "SET_SPECIFIC_COMPANIES"; payload: string[] }
  | { type: "RESET_HYDRATION_VALUES"; payload: ILsnAutopilotHydrationValues };

const initialState: State = {
  prompt: "",
  savedFilters: null,
  expandFilters: null,
  expandPrompt: "",
  expandScoringCriteriaValues: [],
  scoringCriteriaValues: [],
  specificCompanies: [],
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_PROMPT":
      return { ...state, prompt: action.payload };
    case "SET_SAVED_FILTERS":
      return { ...state, savedFilters: action.payload };
    case "SET_EXPAND_FILTERS":
      return { ...state, expandFilters: action.payload };
    case "SET_EXPAND_PROMPT":
      return { ...state, expandPrompt: action.payload };
    case "SET_SCORING_CRITERIA_VALUES":
      return { ...state, scoringCriteriaValues: action.payload };
    case "SET_SPECIFIC_COMPANIES":
      return { ...state, specificCompanies: action.payload };
    case "RESET_HYDRATION_VALUES":
      return {
        ...state,
        prompt: action.payload.prompt || "",
        savedFilters: action.payload.savedFilters || null,
        expandFilters: action.payload.expandFilters || null,
        expandPrompt: action.payload.expandPrompt || "",
        scoringCriteriaValues: action.payload.scoringCriteriaValues || [],
        specificCompanies: action.payload.specificCompanies || [],
      };
    default:
      return state;
  }
};

const LsnAutopilotHydrationContextContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Memoize all setter functions to prevent recreation on every render
  const setPrompt = useCallback((prompt: string) => {
    dispatch({ type: "SET_PROMPT", payload: prompt });
  }, []);

  const setSavedFilters = useCallback(
    (filters: LinkedinSalesNavigatorFilters | null) => {
      dispatch({ type: "SET_SAVED_FILTERS", payload: filters });
    },
    [],
  );

  const setExpandFilters = useCallback(
    (filters: LinkedinSalesNavigatorFilters | null) => {
      dispatch({ type: "SET_EXPAND_FILTERS", payload: filters });
    },
    [],
  );

  const setExpandPrompt = useCallback((prompt: string) => {
    dispatch({ type: "SET_EXPAND_PROMPT", payload: prompt });
  }, []);

  const setExpandScoringCriteriaValues = useCallback((values: string[]) => {
    dispatch({ type: "SET_EXPAND_SCORING_CRITERIA_VALUES", payload: values });
  }, []);

  const setScoringCriteriaValues = useCallback((values: string[]) => {
    dispatch({ type: "SET_SCORING_CRITERIA_VALUES", payload: values });
  }, []);

  const setSpecificCompanies = useCallback((companies: string[]) => {
    dispatch({ type: "SET_SPECIFIC_COMPANIES", payload: companies });
  }, []);

  const resetHydrationValues = useCallback(
    (values: ILsnAutopilotHydrationValues) => {
      dispatch({ type: "RESET_HYDRATION_VALUES", payload: values });
    },
    [],
  );

  const expandContext = useMemo(() => {
    if (!state.expandPrompt) return undefined;

    return {
      remixPrompt: state.expandPrompt,
      previousLinkedinSearchFilters: {
        keywords: state.expandFilters?.keywords,
        jobTitles: state.expandFilters?.jobTitles?.map((j) => ({
          label: j.label,
        })),
        industries: state.expandFilters?.industries?.map((i) => ({
          id: i.id,
          label: i.label,
        })),
        geographies: state.expandFilters?.geographies?.map((g) => ({
          id: g.id,
          label: g.label,
        })),
        companyHQLocations: state.expandFilters?.companyHQLocations?.map(
          (c) => ({
            id: c.id,
            label: c.label,
          }),
        ),
        companyHeadCounts: state.expandFilters?.companyHeadCounts?.map((c) => ({
          id: c.id,
          label: c.label,
        })),
        companyNames: state.expandFilters?.companyNames?.map((c) => ({
          label: c.label,
        })),
      },
    };
  }, [state.expandFilters, state.expandPrompt]);

  // Memoize the entire context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      prompt: state.prompt,
      setPrompt,
      savedFilters: state.savedFilters,
      setSavedFilters,
      expandFilters: state.expandFilters,
      setExpandFilters,
      expandPrompt: state.expandPrompt,
      setExpandPrompt,
      expandScoringCriteriaValues: state.expandScoringCriteriaValues,
      setExpandScoringCriteriaValues,
      scoringCriteriaValues: state.scoringCriteriaValues,
      setScoringCriteriaValues,
      expandContext,
      specificCompanies: state.specificCompanies,
      setSpecificCompanies,
      resetHydrationValues,
    }),
    [
      state.prompt,
      setPrompt,
      state.savedFilters,
      setSavedFilters,
      state.expandFilters,
      setExpandFilters,
      state.expandPrompt,
      setExpandPrompt,
      state.expandScoringCriteriaValues,
      setExpandScoringCriteriaValues,
      state.scoringCriteriaValues,
      setScoringCriteriaValues,
      expandContext,
      state.specificCompanies,
      setSpecificCompanies,
      resetHydrationValues,
    ],
  );

  return (
    <LsnAutopilotHydrationContext.Provider value={contextValue}>
      {children}
    </LsnAutopilotHydrationContext.Provider>
  );
};

export default LsnAutopilotHydrationContextContainer;
